import { useMemo, useState } from "react"
import { useSelector } from "react-redux"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"
import { FiInfo } from "react-icons/fi"

import useT from "utils/hooks/useT"
import PendingApprovalLink from "components/Lims/ChangeControlLink"
import { limsSpecVersions } from "state_management"
import { SPEC_VERSION_STATES } from "state_management/lims/limsSpecVersions"
import { diffSpecVersions, diffValueLabel } from "./specVersionDiff"

// SpecChangeControl (F7, SPEC-039) — the QC method spec is under reviewer→supervisor change
// control. Changing the spec content and saving creates a new DRAFT version SERVER-SIDE (the
// effective spec result-entry reads is untouched until the draft is approved). This section lists
// the spec's version history.
//
// SPEC-063 (#4149): it is rendered by LIMS › Analysis specifications now, beside the editor, and no
// longer by the analysis-definition page — the specification moved out of Setup into its own menu,
// and its history belongs where it is edited. The file still lives here because LIMS › Change
// control imports `SpecVersionDiff` from it; moving it would be churn for no gain.
//
// SPEC-058 B: the reviewer/supervisor e-sign actions are NO LONGER here —
// master data is approved in ONE place, LIMS › Change control (a MEMBER-role reviewer could never
// reach this planner-only page, so the buttons here blocked the approval the server allowed). A
// pending version links there instead.
//
// The PARENT loads the versions (R27: it cannot seed the spec editor until it knows whether a
// pending draft exists, so it must fetch them anyway — this component would only duplicate the
// GET). Capabilities ride on the member dump, so there is no extra fetch for canSign either.
//
// R27 (#4092) also puts the pending version's banner at the top: saving a spec change creates a
// version and leaves the effective spec alone, so without saying so the screen looked as though the
// edit had been thrown away.
//
// #4169 removed the paragraph that explained the versioning above the table — the same account is
// in HELP (LIMS › Analysis specifications), and the pending banner already says what happens to an
// unapproved change at the moment it matters.

// #4173 — how many of the newest versions the table lists.
const VERSION_LIMIT = 10

export default function SpecChangeControl({ analysisDefinitionId }) {
  const { t } = useT("common")
  const versions = useSelector((state) =>
    limsSpecVersions.selectVersionsForDefinition(state, analysisDefinitionId)
  )

  // R27 (#4092): the parent now fetches the versions itself — it has to, because it cannot seed the
  // spec editor until it knows whether a pending draft exists. Fetching here as well would just
  // duplicate that GET on every mount, so this component reads the slice and no longer loads it.
  // SPEC-070: the note names the NEXT version that can be signed, not the newest pending one — a
  // version built on a pending draft cannot be approved yet, so pointing at it led nowhere.
  const pending = useSelector((state) => limsSpecVersions.selectSignableVersion(state, analysisDefinitionId))
  const members = useSelector((state) => state.members)

  // #4173 — only the newest VERSION_LIMIT versions are listed. A specification accumulates a
  // version per approved change and the old ones stop being interesting long before the table
  // stops growing; the full history remains in the audit trail. Each entry keeps its index in the
  // FULL list so the row can still diff against its true predecessor.
  const shownVersions = useMemo(
    () =>
      versions
        .map((version, index) => ({ version, index }))
        .slice(Math.max(0, versions.length - VERSION_LIMIT)),
    [versions]
  )

  if (!analysisDefinitionId) return null

  return (
    <div className="LimsSpecChangeControl">
      {/* R27: the whole answer to "it doesn't save". The change IS saved — as a version that is
          waiting for reviewer and supervisor approval before it becomes the effective spec. Without
          this the screen simply showed the old values back and looked like a lost edit. SPEC-070:
          worded neutrally and naming the author — the signable version need not be yours. */}
      {pending && (
        <p className="LimsSpecChangeControl-pending">
          <FiInfo className="LimsSpecChangeControl-pending-icon" />
          <span>
            {t(
              pending.state === SPEC_VERSION_STATES.REVIEWER_APPROVED
                ? "limsSpecDraftPendingSupervisor"
                : "limsSpecDraftPending",
              {
                n: pending.versionNumber,
                initials: (pending.createdBy && members[pending.createdBy] && members[pending.createdBy].initials) || "—",
              }
            )}
          </span>
          <PendingApprovalLink state={pending.state} itemId={pending.id} />
        </p>
      )}
      {versions.length === 0 ? (
        <p className="LimsSpecChangeControl-empty">{t("limsSpecVersionNone")}</p>
      ) : (
        <Table size="sm" className="LimsSpecChangeControl-table">
          <thead>
            <tr>
              <th>{t("limsForskriftVersion")}</th>
              <th>{t("limsChangeControlCreatedBy")}</th>
              <th>{t("limsVersionBuiltOnHeader")}</th>
              <th>{t("limsForskriftStatus")}</th>
              <th>{t("limsForskriftReviewedBy")}</th>
              <th>{t("limsForskriftSupervisorBy")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {/* R33/R34: each row can expand to show what changed. SPEC-070: against the version it
                was BUILT ON (`baseVersionOf`), not simply the one numbered before it — two
                versions built on the same base are both changes to that base. Rows from before
                SPEC-070 carry no base and fall back to their predecessor. The lookup runs over the
                FULL list, so a shown row keeps its real base even when #4173 hides it. */}
            {shownVersions.map(({ version }) => (
              <SpecVersionRow
                key={version.id}
                version={version}
                previous={limsSpecVersions.baseVersionOf(versions, version)}
              />
            ))}
          </tbody>
        </Table>
      )}
    </div>
  )
}

function SpecVersionRow({ version, previous }) {
  const { t } = useT("common")
  const members = useSelector((state) => state.members)
  const [showDiff, setShowDiff] = useState(false)
  const initials = (sig) =>
    (sig && sig.memberId && members[sig.memberId] && members[sig.memberId].initials) || "—"

  return (
    <>
      <tr>
        <td>v{version.versionNumber}</td>
        {/* SPEC-070: who made the version, and what it was made on top of. */}
        <td>{(version.createdBy && members[version.createdBy] && members[version.createdBy].initials) || "—"}</td>
        <td>{version.basedOnVersionNumber ? `v${version.basedOnVersionNumber}` : "—"}</td>
        <td>
          <span className={"LimsForskrifter-status " + statusClass(version.state)}>
            {t(statusLabelKey(version.state))}
          </span>
        </td>
        <td>{version.reviewerSignature ? initials(version.reviewerSignature) : "—"}</td>
        <td>{version.supervisorSignature ? initials(version.supervisorSignature) : "—"}</td>
        <td className="LimsSpecChangeControl-actions">
          <Button variant="link" size="sm" onClick={() => setShowDiff(!showDiff)}>
            {showDiff ? t("limsSpecDiffHide") : t("limsSpecDiffShow")}
          </Button>
          <PendingApprovalLink state={version.state} itemId={version.id} />
        </td>
      </tr>
      {showDiff && (
        <tr className="LimsSpecChangeControl-diff-row">
          <td colSpan={7}>
            <SpecVersionDiff previous={previous} version={version} />
          </td>
        </tr>
      )}
    </>
  )
}

// R33/R34 (#4098) — what changed in this version, against its predecessor. Built entirely from the
// frozen snapshots the version rows already carry, so no extra request is made to render it.
// Exported (SPEC-058 B): LIMS › Change control renders the same diff for the pending version.
export function SpecVersionDiff({ previous, version }) {
  const { t } = useT("common")
  const diff = diffSpecVersions(previous, version)

  if (diff.isInitial) return <p className="LimsSpecChangeControl-diff-note">{t("limsSpecDiffInitial")}</p>
  // SPEC-070: name the version this one is compared WITH (its base), so a diff is never ambiguous
  // when several versions are pending side by side.
  const comparedWith = previous && (
    <p className="LimsSpecChangeControl-diff-note">{t("limsSpecDiffComparedWith", { n: previous.versionNumber })}</p>
  )
  if (!diff.hasChanges)
    return (
      <>
        {comparedWith}
        <p className="LimsSpecChangeControl-diff-note">{t("limsSpecDiffNone")}</p>
      </>
    )

  const list = (labelKey, items) =>
    items.length > 0 && (
      <li>
        <span className="LimsSpecChangeControl-diff-verb">{t(labelKey)}:</span> {items.join(", ")}
      </li>
    )

  const changedList = (items) =>
    items.map((entry) => (
      <li key={entry.label}>
        <span className="LimsSpecChangeControl-diff-verb">{t("limsSpecDiffChanged")}:</span> {entry.label}
        <ul className="LimsSpecChangeControl-diff-fields">
          {entry.fields.map((f) => (
            <li key={f.labelKey}>
              {t(f.labelKey)}: <del>{diffValueLabel(f.from, t)}</del> → <ins>{diffValueLabel(f.to, t)}</ins>
            </li>
          ))}
        </ul>
      </li>
    ))

  return (
    <div className="LimsSpecChangeControl-diff">
      {comparedWith}
      {(diff.parameters.added.length > 0 ||
        diff.parameters.removed.length > 0 ||
        diff.parameters.changed.length > 0) && (
        <>
          <p className="LimsSpecChangeControl-diff-group">{t("limsSpecification")}</p>
          <ul>
            {list("limsSpecDiffAdded", diff.parameters.added)}
            {list("limsSpecDiffRemoved", diff.parameters.removed)}
            {changedList(diff.parameters.changed)}
          </ul>
        </>
      )}
      {diff.formula && (
        <>
          <p className="LimsSpecChangeControl-diff-group">{t("limsSpecDiffOverallFormula")}</p>
          <ul>
            <li>
              <del>{diffValueLabel(diff.formula.from, t)}</del> → <ins>{diffValueLabel(diff.formula.to, t)}</ins>
            </li>
          </ul>
        </>
      )}
      {/* SPEC-063 (#4149): the consumable requirements were split OUT of the versioned
          specification, so there is no consumables group here any more. Versions created since the
          split carry no requirement snapshot, and diffing against one that does would report every
          requirement as deleted on every new version. */}
    </div>
  )
}

function statusLabelKey(state) {
  if (state === SPEC_VERSION_STATES.REVIEWER_APPROVED) return "limsForskriftStatusReviewerApproved"
  if (state === SPEC_VERSION_STATES.EFFECTIVE) return "limsForskriftStatusEffective"
  if (state === SPEC_VERSION_STATES.SUPERSEDED) return "limsForskriftStatusSuperseded"
  return "limsForskriftStatusDraft"
}

function statusClass(state) {
  if (state === SPEC_VERSION_STATES.EFFECTIVE) return "is-effective"
  if (state === SPEC_VERSION_STATES.REVIEWER_APPROVED) return "is-reviewer"
  if (state === SPEC_VERSION_STATES.SUPERSEDED) return "is-superseded"
  return "is-draft"
}
